import type { FormEntry } from '@tunasong/music-lib'
import type { CSSProperties, FC } from 'react'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import styles from './form-explore-chart.module.css'

export interface FormExploreChartProps {
  form: FormEntry
  /** Index of the section currently being auditioned, or null. */
  activeSectionIndex: number | null
  onSectionClick: (sectionIndex: number) => void
}

/**
 * Horizontal section diagram for Form Explore. Each section renders as a
 * coloured block sized proportionally to its measure count; clicking a block
 * fires `onSectionClick(index)`. The active block (during playback) carries
 * `aria-current="step"` and dims neighbours via CSS.
 *
 * Spec: docs/superpowers/specs/2026-05-23-form-visualizer-v1-design.md §7
 */
export const FormExploreChart: FC<FormExploreChartProps> = props => {
  const { form, activeSectionIndex, onSectionClick } = props
  return (
    <div
      className={styles.chart}
      data-has-active={activeSectionIndex !== null ? 'true' : 'false'}
    >
      {form.sections.map((section, index) => (
        <SectionBlock
          key={`${section.kind}-${index}`}
          lexiconId={section.lexiconId}
          fallbackLabel={section.kind}
          measuresInSection={section.measuresInSection}
          colorToken={section.colorToken}
          active={activeSectionIndex === index}
          onClick={() => onSectionClick(index)}
        />
      ))}
    </div>
  )
}

interface SectionBlockProps {
  lexiconId: string
  fallbackLabel: string
  measuresInSection: number
  colorToken: string
  active: boolean
  onClick: () => void
}

const SectionBlock: FC<SectionBlockProps> = props => {
  const copy = useTheoryCopy(props.lexiconId)
  const label = copy?.displayName ?? props.fallbackLabel
  const labelShort = copy?.displayNameShort ?? props.fallbackLabel
  const ariaLabel = `${label} — ${props.measuresInSection} bars`
  const style: CSSProperties = {
    flexGrow: props.measuresInSection,
    flexBasis: 0,
    backgroundColor: `var(${props.colorToken})`,
  }
  return (
    <button
      type="button"
      onClick={props.onClick}
      aria-label={ariaLabel}
      aria-current={props.active ? 'step' : undefined}
      className={styles.block}
      style={style}
    >
      <span className={styles.blockLabel}>{labelShort}</span>
      <span className={styles.blockBars}>{props.measuresInSection} bars</span>
    </button>
  )
}
